"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { Button, Switch } from "@owlcat/ui";
import { CONSENT_OPEN_EVENT, CONSENT_VERSION, onConsentChange, readConsent, writeConsent, } from "./consent";
export { hasConsent, readConsent, writeConsent, onConsentChange, openConsentBanner, buildConsentCookie, toRecord, parseConsent, hasStoredConsent, CONSENT_VERSION, CONSENT_COOKIE, CONSENT_MAX_AGE, } from "./consent";
/**
 * Reactive consent for client components. Reads the shared parent-domain cookie
 * and re-renders on same-tab changes (banner writes) or a version bump. Works
 * WITHOUT a provider — mounting <CookieConsent/> once per app is enough.
 */
export function useConsent() {
    const [consent, setConsent] = useState(null);
    const [ready, setReady] = useState(false);
    useEffect(() => {
        setConsent(readConsent());
        setReady(true);
        return onConsentChange((record) => setConsent(record));
    }, []);
    const has = useCallback((category) => {
        if (category === "essential")
            return true;
        return consent ? consent.categories[category] === true : false;
    }, [consent]);
    return { consent, hasConsent: has, ready };
}
/**
 * Fleet copy, one place. The banner used to render English on every app because
 * no app ever passed `labels` (prod 2026-09: a Russian page with an English
 * cookie prompt), so the translations live HERE and are picked by locale:
 * explicit `locale` prop → <html lang> → 'en'. `labels` still overrides.
 */
const LOCALE_LABELS = {
    ru: {
        title: "Мы используем cookie",
        description: "Мы используем cookie, без которых сайт не работает, а также необязательные — для настроек интерфейса и продуктовой аналитики. Можно принять все, оставить только необходимые или выбрать вручную.",
        acceptAll: "Принять все",
        essentialOnly: "Только необходимые",
        customize: "Настроить",
        save: "Сохранить выбор",
        essentialLabel: "Строго необходимые",
        essentialDescription: "Вход, сессия, язык и ваш выбор по cookie. Всегда включены.",
        functionalLabel: "Функциональные",
        functionalDescription: "Запоминают настройки сверх необходимого (например, интерфейс и параметры игр).",
        analyticsLabel: "Аналитика",
        analyticsDescription: "Анонимная продуктовая аналитика — помогает нам улучшать приложения.",
        privacyLinkLabel: "Политика конфиденциальности и cookie",
    },
    uk: {
        title: "Ми використовуємо cookie",
        description: "Ми використовуємо cookie, без яких сайт не працює, а також необов'язкові — для налаштувань інтерфейсу та продуктової аналітики. Можна прийняти всі, залишити лише необхідні або обрати вручну.",
        acceptAll: "Прийняти всі",
        essentialOnly: "Лише необхідні",
        customize: "Налаштувати",
        save: "Зберегти вибір",
        essentialLabel: "Суворо необхідні",
        essentialDescription: "Вхід, сесія, мова та ваш вибір щодо cookie. Завжди увімкнені.",
        functionalLabel: "Функціональні",
        functionalDescription: "Запам'ятовують налаштування понад необхідні (наприклад, інтерфейс і параметри ігор).",
        analyticsLabel: "Аналітика",
        analyticsDescription: "Анонімна продуктова аналітика — допомагає нам покращувати застосунки.",
        privacyLinkLabel: "Політика конфіденційності та cookie",
    },
    cs: {
        title: "Používáme cookies",
        description: "Používáme cookies nezbytné pro provoz webu a volitelné cookies pro předvolby rozhraní a produktovou analytiku. Můžete přijmout vše, ponechat jen nezbytné, nebo si vybrat.",
        acceptAll: "Přijmout vše",
        essentialOnly: "Jen nezbytné",
        customize: "Nastavit",
        save: "Uložit volbu",
        essentialLabel: "Nezbytně nutné",
        essentialDescription: "Přihlášení, relace, jazyk a vaše volba cookies. Vždy zapnuté.",
        functionalLabel: "Funkční",
        functionalDescription: "Pamatují si předvolby nad rámec nezbytných (např. rozhraní a nastavení her).",
        analyticsLabel: "Analytika",
        analyticsDescription: "Anonymní produktová analytika — pomáhá nám aplikace zlepšovat.",
        privacyLinkLabel: "Zásady ochrany soukromí a cookies",
    },
    de: {
        title: "Wir verwenden Cookies",
        description: "Wir verwenden Cookies, die für den Betrieb der Website notwendig sind, sowie optionale Cookies für Einstellungen und Produktanalyse. Du kannst alle akzeptieren, nur die notwendigen behalten oder selbst auswählen.",
        acceptAll: "Alle akzeptieren",
        essentialOnly: "Nur notwendige",
        customize: "Anpassen",
        save: "Auswahl speichern",
        essentialLabel: "Unbedingt erforderlich",
        essentialDescription: "Anmeldung, Sitzung, Sprache und deine Cookie-Auswahl. Immer aktiv.",
        functionalLabel: "Funktional",
        functionalDescription: "Merkt sich Einstellungen über das Nötigste hinaus (z. B. Oberfläche und Spieleinstellungen).",
        analyticsLabel: "Analyse",
        analyticsDescription: "Anonyme Produktanalyse — hilft uns, die Apps zu verbessern.",
        privacyLinkLabel: "Datenschutz- und Cookie-Richtlinie",
    },
    fr: {
        title: "Nous utilisons des cookies",
        description: "Nous utilisons des cookies strictement nécessaires au fonctionnement du site, ainsi que des cookies facultatifs pour les préférences d'interface et l'analyse produit. Vous pouvez tout accepter, ne garder que l'essentiel ou choisir.",
        acceptAll: "Tout accepter",
        essentialOnly: "Essentiels uniquement",
        customize: "Personnaliser",
        save: "Enregistrer mes choix",
        essentialLabel: "Strictement nécessaires",
        essentialDescription: "Connexion, session, langue et votre choix de cookies. Toujours actifs.",
        functionalLabel: "Fonctionnels",
        functionalDescription: "Mémorisent des préférences au-delà de l'essentiel (interface, paramètres de jeu).",
        analyticsLabel: "Analyse",
        analyticsDescription: "Analyse produit anonyme — nous aide à améliorer les applications.",
        privacyLinkLabel: "Politique de confidentialité et cookies",
    },
};
/** English defaults — also the fallback for a locale we have no copy for. */
const DEFAULT_LABELS = {
    title: "We use cookies",
    description: "We use cookies that are strictly necessary to run the site, plus optional cookies for functional preferences and product analytics. You can accept all, keep only the essential ones, or choose which optional cookies to allow.",
    acceptAll: "Accept all",
    essentialOnly: "Essential only",
    customize: "Customize",
    save: "Save choices",
    essentialLabel: "Strictly necessary",
    essentialDescription: "Sign-in, session, language and your cookie choice. Always on.",
    functionalLabel: "Functional",
    functionalDescription: "Remembers preferences beyond the essentials (e.g. UI and game settings).",
    analyticsLabel: "Analytics",
    analyticsDescription: "Anonymous product analytics to help us improve the apps.",
    privacyLinkLabel: "Privacy & Cookie Policy",
};
/**
 * The fleet's single, shared cookie-consent banner. Reads the parent-domain
 * `owlcat-consent` cookie; when absent or older than CONSENT_VERSION it renders
 * an accessible design-system banner (Accept all / Essential only / Customize).
 * On a choice it writes the shared cookie so every sibling subdomain honors it,
 * and — for logged-in users — best-effort POSTs a proof-of-consent record.
 *
 * Contrast is carried entirely by the @owlcat/ui + @owlcat/theme DS tokens
 * (bg-card / border-border / *-foreground), correct in BOTH light and dark; no
 * bespoke color overrides (portal's earlier contrast complaint came from hand-
 * rolled colors — avoided here).
 */
export function CookieConsent({ parentDomain, recordUrl, identityId, privacyHref, locale, labels, writeOptions, }) {
    const [mounted, setMounted] = useState(false);
    // <html lang> is the app's rendered locale and the fallback for apps that do
    // not pass `locale`. Read lazily on the client — the banner renders nothing
    // until `mounted`, so there is no hydration text to mismatch.
    const [htmlLang] = useState(() => typeof document === "undefined" ? null : document.documentElement.lang || null);
    const lang = (locale ?? htmlLang ?? "en").slice(0, 2).toLowerCase();
    const t = { ...DEFAULT_LABELS, ...(LOCALE_LABELS[lang] ?? {}), ...labels };
    const [open, setOpen] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [functional, setFunctional] = useState(false);
    const [analytics, setAnalytics] = useState(false);
    // First-visit consent (no prior choice) must NOT be dismissable — the user has
    // to make a choice. But when re-opened via the "Cookie settings" link (the user
    // already consented), it IS dismissable: click outside / Escape keeps the
    // existing saved prefs unchanged.
    const [dismissable, setDismissable] = useState(false);
    useEffect(() => {
        setMounted(true);
        const existing = readConsent();
        if (!existing) {
            setOpen(true);
        }
        else {
            setFunctional(existing.categories.functional === true);
            setAnalytics(existing.categories.analytics === true);
        }
        // Allow any app chrome to re-open the banner (e.g. a "Cookie settings" link).
        const reopen = () => {
            const cur = readConsent();
            setFunctional(cur?.categories.functional === true);
            setAnalytics(cur?.categories.analytics === true);
            setShowDetails(true);
            setDismissable(true); // re-open = already consented → outside-click/Escape closes
            setOpen(true);
        };
        window.addEventListener(CONSENT_OPEN_EVENT, reopen);
        return () => window.removeEventListener(CONSENT_OPEN_EVENT, reopen);
    }, []);
    // Close without changing saved prefs — only for the dismissable (settings)
    // re-open; first-visit consent stays until the user makes a choice.
    const dismiss = useCallback(() => {
        if (!dismissable)
            return;
        setOpen(false);
        setShowDetails(false);
    }, [dismissable]);
    useEffect(() => {
        if (!open || !dismissable)
            return;
        const onKey = (e) => {
            if (e.key === "Escape")
                dismiss();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, dismissable, dismiss]);
    const persist = useCallback((categories) => {
        writeConsent(categories, { parentDomain, ...writeOptions });
        // Best-effort proof-of-consent record for logged-in users. The banner is
        // fully functional cookie-only; a failed/absent call never blocks.
        if (recordUrl && identityId) {
            try {
                void fetch(recordUrl, {
                    method: "POST",
                    credentials: "include",
                    keepalive: true,
                    headers: { "content-type": "application/json" },
                    body: JSON.stringify({
                        identityId,
                        consentVersion: CONSENT_VERSION,
                        categories: { essential: true, ...categories },
                    }),
                }).catch(() => { });
            }
            catch {
                /* best-effort */
            }
        }
        setOpen(false);
        setShowDetails(false);
    }, [parentDomain, writeOptions, recordUrl, identityId]);
    if (!mounted || !open)
        return null;
    return (_jsxs(_Fragment, { children: [dismissable && (_jsx("div", { className: "fixed inset-0 z-[99] bg-black/20", onClick: dismiss, "data-testid": "cookie-consent-backdrop", "aria-hidden": true })), _jsx("div", { className: "fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4", role: "dialog", "aria-modal": "false", "aria-labelledby": "owlcat-consent-title", "aria-describedby": "owlcat-consent-desc", "data-testid": "cookie-consent-banner", children: _jsx("div", { className: "mx-auto max-w-4xl rounded-lg border border-border bg-card text-card-foreground shadow-2xl", children: _jsxs("div", { className: "p-4 sm:p-6", children: [_jsx("h2", { id: "owlcat-consent-title", className: "text-base font-semibold sm:text-lg", children: t.title }), _jsxs("p", { id: "owlcat-consent-desc", className: "mt-2 text-sm text-muted-foreground", children: [t.description, privacyHref ? (_jsxs(_Fragment, { children: [" ", _jsx("a", { href: privacyHref, className: "underline underline-offset-2 hover:text-foreground", children: t.privacyLinkLabel })] })) : null] }), showDetails ? (_jsxs("ul", { className: "mt-4 space-y-3", "data-testid": "cookie-consent-details", children: [_jsxs("li", { className: "flex items-start justify-between gap-4", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "text-sm font-medium", children: t.essentialLabel }), _jsx("p", { className: "text-xs text-muted-foreground", children: t.essentialDescription })] }), _jsx(Switch, { checked: true, disabled: true, "aria-label": t.essentialLabel })] }), _jsxs("li", { className: "flex items-start justify-between gap-4", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "text-sm font-medium", children: t.functionalLabel }), _jsx("p", { className: "text-xs text-muted-foreground", children: t.functionalDescription })] }), _jsx(Switch, { checked: functional, onCheckedChange: setFunctional, "aria-label": t.functionalLabel, "data-testid": "cookie-consent-functional" })] }), _jsxs("li", { className: "flex items-start justify-between gap-4", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "text-sm font-medium", children: t.analyticsLabel }), _jsx("p", { className: "text-xs text-muted-foreground", children: t.analyticsDescription })] }), _jsx(Switch, { checked: analytics, onCheckedChange: setAnalytics, "aria-label": t.analyticsLabel, "data-testid": "cookie-consent-analytics" })] })] })) : null, _jsx("div", { className: "mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end", children: showDetails ? (_jsx(Button, { variant: "default", onClick: () => persist({ functional, analytics }), "data-testid": "cookie-consent-save", children: t.save })) : (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "outline", onClick: () => persist({ functional: false, analytics: false }), "data-testid": "cookie-consent-essential", children: t.essentialOnly }), _jsx(Button, { variant: "secondary", onClick: () => setShowDetails(true), "data-testid": "cookie-consent-customize", children: t.customize }), _jsx(Button, { variant: "default", onClick: () => persist({ functional: true, analytics: true }), "data-testid": "cookie-consent-accept-all", children: t.acceptAll })] })) })] }) }) })] }));
}
